import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"
import { DirectUpload } from "@rails/activestorage"

// Uploads files straight from the browser to the storage service, then posts
// only the signed blob IDs to Rails. Keeps the multi-megabyte transfer off the
// app request, so a slow bucket can't blow past the proxy's response timeout.
export default class extends Controller {
  static values = { url: String, directUploadUrl: String }

  async submit(event) {
    const input = event.target
    const files = Array.from(input.files || [])
    if (!files.length) return

    const category = document.getElementById("project-image-category")?.value || ""
    const gallery  = document.getElementById("project-gallery") || document.getElementById("vehicle-gallery")
    const status   = document.getElementById("image-upload-status")

    const skeletons = files.map(file => this.#createSkeleton(file))
    if (gallery) {
      gallery.classList.add("bg-base-300", "border", "border-base-300")
      skeletons.forEach(s => gallery.appendChild(s.el))
    }

    input.disabled = true
    const label = files.length === 1 ? "1 file" : `${files.length} files`

    // Weight overall progress by byte count so one big photo doesn't get
    // averaged away by several small ones.
    const totalBytes = files.reduce((sum, f) => sum + f.size, 0) || 1
    const loaded = files.map(() => 0)
    const reportProgress = () => {
      const pct = Math.round((loaded.reduce((a, b) => a + b, 0) / totalBytes) * 100)
      this.#setStatus(status, `▸ UPLOADING · ${label} · ${pct}%`)
    }
    reportProgress()

    // allSettled, not all: one rejected file shouldn't throw away the ones
    // that already made it to storage.
    const results = await Promise.allSettled(files.map((file, i) =>
      this.#directUpload(file, bytes => {
        loaded[i] = bytes
        skeletons[i].setProgress(Math.round((bytes / (file.size || 1)) * 100))
        reportProgress()
      })
    ))

    const signedIds = results.filter(r => r.status === "fulfilled").map(r => r.value)
    const failed = results.length - signedIds.length

    if (!signedIds.length) {
      input.disabled = false
      skeletons.forEach(s => s.cleanup())
      this.#setStatus(status, "▸ UPLOAD FAILED · storage rejected the files", true)
      return
    }

    this.#setStatus(status, `▸ PROCESSING · ${label}`)
    skeletons.forEach(s => s.setProcessing())

    const formData = new FormData()
    signedIds.forEach(id => formData.append("files[]", id))
    if (category) formData.append("category", category)

    const xhr = new XMLHttpRequest()
    xhr.open("POST", this.urlValue)
    xhr.setRequestHeader("X-CSRF-Token", this.#csrfToken())
    xhr.setRequestHeader("Accept", "text/vnd.turbo-stream.html")

    xhr.addEventListener("load", () => {
      input.disabled = false
      input.value = ""
      skeletons.forEach(s => s.cleanup())
      if (xhr.status >= 200 && xhr.status < 300) {
        Turbo.renderStreamMessage(xhr.responseText)
        this.#setStatus(status, failed ? `▸ ${failed} of ${files.length} failed to upload` : "", failed > 0)
        document.getElementById("image-upload-empty-hint")?.remove()
      } else {
        this.#setStatus(status, `▸ UPLOAD FAILED · ${xhr.status}`, true)
      }
    })

    xhr.addEventListener("error", () => {
      input.disabled = false
      skeletons.forEach(s => s.cleanup())
      this.#setStatus(status, "▸ UPLOAD FAILED · network error", true)
    })

    xhr.send(formData)
  }

  #directUpload(file, onProgress) {
    return new Promise((resolve, reject) => {
      const upload = new DirectUpload(file, this.directUploadUrlValue, {
        directUploadWillStoreFileWithXHR(request) {
          request.upload.addEventListener("progress", (e) => {
            if (e.lengthComputable) onProgress(e.loaded)
          })
        }
      })

      upload.create((error, blob) => {
        if (error) {
          reject(error)
        } else {
          onProgress(file.size)
          resolve(blob.signed_id)
        }
      })
    })
  }

  #csrfToken() {
    return document.querySelector("[name='csrf-token']")?.content || ""
  }

  #createSkeleton(file) {
    const url = URL.createObjectURL(file)
    const el  = document.createElement("div")
    el.className = "relative aspect-square bg-base-050 overflow-hidden"
    el.innerHTML = `
      <img src="${url}" class="w-full h-full object-cover opacity-40" alt="">
      <div class="absolute inset-0 flex flex-col items-center justify-center gap-2" style="background:rgba(14,14,14,0.55);">
        <div class="pt-mono-label pt-mono-label--accent text-[10px]" data-role="label">UPLOADING</div>
        <div class="pt-mono-label text-[11px] tracking-[1px]" data-role="pct">0%</div>
        <div class="w-3/4 h-[2px] bg-base-300">
          <div class="h-full bg-primary transition-[width] duration-150" style="width:0%" data-role="bar"></div>
        </div>
      </div>
    `
    const bar   = el.querySelector('[data-role="bar"]')
    const pct   = el.querySelector('[data-role="pct"]')
    const label = el.querySelector('[data-role="label"]')

    return {
      el,
      setProgress(p) {
        if (bar) bar.style.width = `${p}%`
        if (pct) pct.textContent = `${p}%`
      },
      setProcessing() {
        if (label) label.textContent = "PROCESSING"
        if (pct)   pct.textContent   = "—"
        if (bar)   bar.style.width   = "100%"
      },
      cleanup() {
        URL.revokeObjectURL(url)
        el.remove()
      }
    }
  }

  #setStatus(el, text, isError = false) {
    if (!el) return
    el.textContent = text
    el.style.color = isError ? "var(--color-error)" : ""
  }
}
